import {
  useCreateMineModelMutation,
  useUpdateMineModelMutation
} from 'providers/api';
import { trackEvent } from 'providers/analytics';
import React from 'react';
import { i18n } from '@lingui/core';
import { isReticulationImportFile, toReticulationImportBody } from './reticulationImportBody';

const reticulationErrors = {
  incomplete: 'The reticulation data provided is incomplete.',
  server: 'There was an issue uploading the reticulation data to the server.',
};

const error = i18n._('Project Id not found');
const useSaveReticulationData = (
  onSuccess: () => void,
  projectId?: string,
  projectName?: string,
  hasReticulationData = false,
) => {
  const updateReticulationMutation = useUpdateMineModelMutation(projectId ?? '');
  const createReticulationMutation = useCreateMineModelMutation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [errors, setErrors] = React.useState<string[]>([]);

  const save = (rawJSON: unknown) => {
    setErrors([]);
    if (rawJSON) {
      if (isReticulationImportFile(rawJSON)) {
        if (!(projectId && projectName)) {
          throw new Error(error);
        }

        const reticulationSaveData = {
          projectId,
          projectName,
          data: toReticulationImportBody(rawJSON),
        };

        // attempt mutation
        const mutation = hasReticulationData ? updateReticulationMutation : createReticulationMutation;
        setIsSubmitting(true);
        mutation.mutate(reticulationSaveData, {
          onSuccess: () => {
            trackEvent('data_imported', { project_id: projectId, import_type: 'reticulation' });
            onSuccess();
            setErrors([]);
          },
          onError: () => {
            setErrors((existingErrors) => [...existingErrors, reticulationErrors.server]);
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        });
      } else {
        setErrors((existingErrors) => [...existingErrors, reticulationErrors.incomplete]);
      }
    }
  };

  return { save, errors, isSubmitting };
};

export default useSaveReticulationData;
